import * as Sentry from "@sentry/react";
import type { AssignmentPlatform } from "refsix-core";
import type { ScrapeDisposition, ScrapeMessage } from "./inAppScrapeMessages";

/**
 * Why a scrape is being reported. `unrecognisedSchedulePage` is the user giving
 * up on the schedule URL itself with no adapter claiming it — the usual symptom
 * of a markup change, but also what an empty MOAS schedule looks like (its
 * adapter needs a fixture link to claim the page), hence a warning, not an error.
 */
// oxlint-disable-next-line refsix/no-enum-outside-models -- extends ScrapeDisposition's reasons with one only this report uses
export type ScrapeFailureReason =
    | Exclude<
          Extract<ScrapeDisposition, { act: "failed" }>["reason"],
          "allInPast"
      >
    | "unrecognisedSchedulePage";

export interface ScrapeFailure {
    reason: ScrapeFailureReason;
    /** The scrape that failed, or the last one before the user gave up. */
    message: ScrapeMessage;
    found?: number;
    diagnostics?: string;
    /** Whether the import navigated the WebView to the schedule itself. */
    redirectedToSchedule: boolean;
    /** Scrape messages received this import — roughly, pages loaded. */
    pagesSeen: number;
}

type ReportedPlatform = Pick<AssignmentPlatform, "id" | "displayName">;

export function scrapeFailureTitle(platform: ReportedPlatform): string {
    return `${platform.displayName} scraping failed`;
}

/**
 * One Sentry issue per platform and reason, with the page attached, so that
 * when Arbiter or MOAS changes its markup we hear about it and can see what
 * changed — rather than referees quietly closing a WebView that never imports.
 */
export function reportScrapeFailure(
    platform: ReportedPlatform,
    failure: ScrapeFailure
): void {
    const { message } = failure;
    const page = message.page;

    Sentry.withScope((scope) => {
        scope.setLevel(
            failure.reason === "unrecognisedSchedulePage" ? "warning" : "error"
        );
        scope.setFingerprint([
            "assignment-scrape-failed",
            platform.id,
            failure.reason,
        ]);
        scope.setTag("assignment_platform", platform.id);
        scope.setTag("scrape_failure", failure.reason);
        if (page?.httpStatus !== undefined) {
            scope.setTag("http_status", String(page.httpStatus));
        }

        scope.setContext("scrape", {
            url: message.url,
            httpStatus: page?.httpStatus,
            title: page?.title,
            reason: failure.reason,
            resultStatus: message.result?.status,
            error: message.error,
            diagnostics: failure.diagnostics,
            found: failure.found,
            redirectedToSchedule: failure.redirectedToSchedule,
            pagesSeen: failure.pagesSeen,
            htmlLength: page?.htmlLength,
            htmlTruncated: page?.truncated,
        });

        if (page) {
            scope.addAttachment({
                filename: `${platform.id}-page.html`,
                data: page.html,
                contentType: "text/html",
            });
        }

        Sentry.captureMessage(scrapeFailureTitle(platform));
    });
}
